package com.evoapps.evoshift.navigation

import com.evoapps.evoshift.Route
import com.evoapps.evoshift.domain.model.Capability
import kotlin.reflect.KClass

/**
 * The bottom bar's (and the rail's) tabs, as in EVoLabels: at most five, chosen by what the
 * person may do at the current workplace ([tabsFor]); everything else is one tap away under
 * [MORE] (the hub, like EVoLabels' Settings tab), so nothing is unreachable.
 *
 * Each tab is a section with its own back stack (kept when switching tabs, see [tabMove]);
 * [HOME] is the root under every other tab, so back from a tab returns home.
 */
enum class AppTab(val root: Route) {
    HOME(Route.Dashboard),
    MY_SHIFTS(Route.Schedule()),
    ROSTERS(Route.RosterManagement),
    TEAM(Route.Team),
    REQUESTS(Route.Requests),
    MONEY(Route.Financials),
    AVAILABILITY(Route.Availability),
    ABSENCES(Route.Absences),
    MORE(Route.More);

    val rootClass: KClass<out Route> get() = root::class
}

/** Most tabs a bar holds; more would squeeze the labels (Polish ones are long). */
const val MAX_TABS = 5

/**
 * The tabs for [capabilities] (see [com.evoapps.evoshift.domain.model.capabilitiesAt]):
 * - owner, financial manager: Home · Rosters · Team · Money · More
 * - manager: Home · Rosters · Team · Requests · More
 * - employee: Home · My shifts · Availability · Absences · More
 * - nothing (no workplace, ended engagement): Home · More
 */
fun tabsFor(capabilities: Set<Capability>): List<AppTab> = buildList {
    add(AppTab.HOME)
    if (Capability.EDIT_ROSTER in capabilities) {
        add(AppTab.ROSTERS)
        if (Capability.MANAGE_TEAM in capabilities) add(AppTab.TEAM)
        when {
            Capability.VIEW_FINANCES in capabilities -> add(AppTab.MONEY)
            Capability.APPROVE_REQUESTS in capabilities -> add(AppTab.REQUESTS)
        }
    } else if (Capability.WORK_SHIFTS in capabilities) {
        add(AppTab.MY_SHIFTS)
        add(AppTab.AVAILABILITY)
        add(AppTab.ABSENCES)
    }
    add(AppTab.MORE)
}

/** The rows of the More hub, in their order. */
enum class MoreEntry {
    // Sections that did not fit in the bar.
    MY_SHIFTS, AVAILABILITY, ABSENCES, REQUESTS, MONEY,
    // SH-257: confirming the actual working hours (owners and managers).
    WORK_HOURS,
    // The team's posts (SH-118).
    ANNOUNCEMENTS,
    // SH-122: the workplace's policies (to read and confirm; managers publish).
    POLICIES,
    // SH-126: training courses and the OHS training record.
    TRAINING,
    // SH-124: tips (own share; managers the pools). No tab of its own.
    TIPS,
    // SH-210: hiring for a post's recruiters who are not managers (managers reach it from Team).
    HIRING,
    // The workplace.
    SWITCH_WORKPLACE, WORKPLACE_SETTINGS, CHECKLISTS, RECURRING_COSTS, SALES_IMPORT, ADD_WORKPLACE,
    // The account; SH-239: "Recommend EVoShift to another employer" (not for owners).
    NOTIFICATIONS, PROFILE, RECOMMEND, SUBSCRIPTION
}

/**
 * What the More hub lists for [capabilities] next to [tabs]: the sections without a tab of their
 * own, the workplace's settings and the account. [hasWorkplace]: the switcher only helps then.
 */
fun moreEntriesFor(
    capabilities: Set<Capability>,
    tabs: List<AppTab>,
    hasWorkplace: Boolean,
    /** SH-210: the person recruits for a job post here (shown to those without the Team tab's hiring). */
    recruiting: Boolean = false
): List<MoreEntry> = buildList {
    fun section(entry: MoreEntry, tab: AppTab, allowed: Boolean) {
        if (allowed && tab !in tabs) add(entry)
    }
    section(MoreEntry.MY_SHIFTS, AppTab.MY_SHIFTS, Capability.WORK_SHIFTS in capabilities)
    section(MoreEntry.AVAILABILITY, AppTab.AVAILABILITY, Capability.WORK_SHIFTS in capabilities)
    section(MoreEntry.ABSENCES, AppTab.ABSENCES, Capability.WORK_SHIFTS in capabilities)
    section(MoreEntry.REQUESTS, AppTab.REQUESTS, Capability.APPROVE_REQUESTS in capabilities)
    section(MoreEntry.MONEY, AppTab.MONEY, Capability.VIEW_FINANCES in capabilities)
    if (hasWorkplace && Capability.EDIT_ROSTER in capabilities) add(MoreEntry.WORK_HOURS)
    if (hasWorkplace && Capability.WORK_SHIFTS in capabilities) add(MoreEntry.ANNOUNCEMENTS)
    if (Capability.WORK_SHIFTS in capabilities) add(MoreEntry.TIPS)
    if (hasWorkplace && recruiting && Capability.MANAGE_TEAM !in capabilities) add(MoreEntry.HIRING)
    if (hasWorkplace) add(MoreEntry.SWITCH_WORKPLACE)
    if (hasWorkplace && Capability.WORK_SHIFTS in capabilities) add(MoreEntry.POLICIES)
    if (hasWorkplace && Capability.WORK_SHIFTS in capabilities) add(MoreEntry.TRAINING)
    if (hasWorkplace && Capability.EDIT_WORKPLACE_SETTINGS in capabilities) add(MoreEntry.WORKPLACE_SETTINGS)
    // SH-121: opening/closing checklists, the managers'.
    if (hasWorkplace && Capability.EDIT_ROSTER in capabilities) add(MoreEntry.CHECKLISTS)
    if (Capability.MANAGE_COST_RULES in capabilities) add(MoreEntry.RECURRING_COSTS)
    // SH-123: takings from a file or the till.
    if (hasWorkplace && Capability.VIEW_FINANCES in capabilities) add(MoreEntry.SALES_IMPORT)
    add(MoreEntry.ADD_WORKPLACE)
    add(MoreEntry.NOTIFICATIONS)
    add(MoreEntry.PROFILE)
    // SH-239: the people working somewhere recommend the app to another employer; owners have their plan.
    if (hasWorkplace && Capability.BILLING !in capabilities) add(MoreEntry.RECOMMEND)
    if (Capability.BILLING in capabilities) add(MoreEntry.SUBSCRIPTION)
}

/**
 * SH-164: the bar's badges: unread notifications on More, and the requests waiting for a decision
 * on the Requests tab, or on More when Requests is one of its rows (owners and financial managers
 * have Money in its place). [pendingRequests] is 0 for those who do not decide requests.
 */
fun tabBadges(tabs: List<AppTab>, unreadNotifications: Int, pendingRequests: Int): Map<AppTab, Int> = buildMap {
    val requestsInBar = AppTab.REQUESTS in tabs
    if (requestsInBar && pendingRequests > 0) put(AppTab.REQUESTS, pendingRequests)
    put(AppTab.MORE, unreadNotifications + if (requestsInBar) 0 else pendingRequests)
}

/** The section a More row opens, or null for rows that are not a route (the switcher). */
fun MoreEntry.route(workplaceId: String?): Route? = when (this) {
    MoreEntry.MY_SHIFTS -> Route.Schedule()
    MoreEntry.AVAILABILITY -> Route.Availability
    MoreEntry.ABSENCES -> Route.Absences
    MoreEntry.REQUESTS -> Route.Requests
    MoreEntry.MONEY -> Route.Financials
    MoreEntry.WORK_HOURS -> workplaceId?.let { Route.WorkHours(it) }
    MoreEntry.ANNOUNCEMENTS -> Route.Announcements
    MoreEntry.POLICIES -> workplaceId?.let { Route.Policies(it) }
    MoreEntry.TRAINING -> workplaceId?.let { Route.Courses(it) }
    MoreEntry.TIPS -> Route.Tips
    MoreEntry.HIRING -> Route.Hiring
    MoreEntry.SWITCH_WORKPLACE -> null
    MoreEntry.WORKPLACE_SETTINGS -> workplaceId?.let { Route.WorkplaceSettings(it) }
    MoreEntry.CHECKLISTS -> Route.Checklists()
    MoreEntry.RECURRING_COSTS -> Route.RecurringCosts
    MoreEntry.SALES_IMPORT -> Route.SalesImport
    MoreEntry.ADD_WORKPLACE -> Route.CreateWorkplace
    MoreEntry.NOTIFICATIONS -> Route.Notifications
    MoreEntry.PROFILE -> Route.Profile
    MoreEntry.RECOMMEND -> Route.Recommend
    MoreEntry.SUBSCRIPTION -> Route.Subscription
}

/**
 * The selected tab for a back stack (oldest first; null for entries that are no route of ours):
 * the tab whose root is nearest the top, so a detail keeps its section highlighted, a section
 * opened from More keeps More highlighted, and anything else belongs to Home.
 */
fun selectedTab(backStack: List<KClass<out Route>?>, tabs: List<AppTab>): AppTab =
    backStack.asReversed().firstNotNullOfOrNull { route -> tabs.firstOrNull { it.rootClass == route } }
        ?: AppTab.HOME

/** What tapping a tab (or opening a section that has a tab) does. */
sealed interface TabMove {
    /** Already at the tab's root. */
    data object Stay : TabMove

    /** Reselecting a tab returns to its root. */
    data class PopToRoot(val tab: AppTab) : TabMove

    /**
     * Another tab: [saveCurrent] keeps the tab being left (Home's stack is the base, not saved),
     * [restore] brings back the target's stack (Home is always just its root); a root with other
     * arguments (the marketplace) starts fresh.
     */
    data class Switch(val tab: AppTab, val root: Route, val saveCurrent: Boolean, val restore: Boolean) : TabMove
}

/**
 * Where to go for [target] (opened at [root]) from [selected], with [current] on top.
 */
fun tabMove(target: AppTab, selected: AppTab, current: KClass<out Route>?, root: Route = target.root): TabMove = when {
    target == selected && root == target.root ->
        if (current == target.rootClass) TabMove.Stay else TabMove.PopToRoot(target)
    target == selected -> TabMove.Switch(target, root, saveCurrent = false, restore = false)
    // Home is never restored: popping up to it with saveState also files the stack being left
    // under Home's id, so restoring Home would bring that tab's screens back on top of it.
    else -> TabMove.Switch(
        target, root,
        saveCurrent = selected != AppTab.HOME,
        restore = root == target.root && target != AppTab.HOME
    )
}

/** Sections: the screens the bar shows on (every tab's root, also when reached through More). */
val sectionRoutes: Set<KClass<out Route>> = AppTab.entries.mapTo(mutableSetOf()) { it.rootClass }

/** Screens without any navigation around them: signing in, the first run, the plans, an invitation. */
val fullScreenRoutes: Set<KClass<out Route>> = setOf(
    Route.Login::class, Route.Onboarding::class, Route.Paywall::class, Route.JoinTeam::class
)

/** What frames the screen: nothing, the phone's bottom bar or the wide screen's rail. */
enum class NavChrome { NONE, BAR, RAIL }

/**
 * The bar shows on sections only; details and forms take the whole phone screen (EVoLabels:
 * the shell only around main screens). Wide screens keep the rail everywhere in the app.
 */
fun navChromeFor(current: KClass<out Route>?, isWide: Boolean, tabletop: Boolean = false): NavChrome = when {
    current == null || current in fullScreenRoutes -> NavChrome.NONE
    // SH-102: a foldable like a laptop: the bottom bar below the hinge, never a rail across it.
    tabletop -> if (current in sectionRoutes) NavChrome.BAR else NavChrome.NONE
    isWide -> NavChrome.RAIL
    current in sectionRoutes -> NavChrome.BAR
    else -> NavChrome.NONE
}

/**
 * Screens with navy under the status bar (a navy app bar or a dark full-screen background); the
 * others are cream. Decides the status bar's icon colour (SH-83).
 */
val navyTopRoutes: Set<KClass<out Route>> = setOf(
    Route.Login::class, Route.Onboarding::class, Route.Paywall::class, Route.CreateWorkplace::class,
    Route.CreateRoster::class, Route.RosterDetail::class, Route.RosterSkeleton::class,
    Route.AvailabilityForm::class, Route.WeeklyTemplates::class
)

/** Light status bar icons on navy screens, dark ones on the cream screens (also before the first screen: the splash is navy). */
fun darkStatusIconsFor(current: KClass<out Route>?): Boolean = current != null && current !in navyTopRoutes

/** The gesture bar sits on the navy bottom bar, else on the screen. */
fun darkNavigationIconsFor(current: KClass<out Route>?, chrome: NavChrome): Boolean =
    chrome != NavChrome.BAR && darkStatusIconsFor(current)

/** Screens of one workplace (or one of its months): they go when the workplace changes. */
val workplaceRoutes: Set<KClass<out Route>> = setOf(
    Route.RosterDetail::class, Route.RosterSkeleton::class, Route.CreateRoster::class, Route.RosterImport::class,
    Route.WorkplaceSettings::class, Route.SkeletonEditor::class, Route.EmployeeForm::class, Route.StaffImport::class,
    Route.DailyRecords::class, Route.Payroll::class, Route.Statistics::class, Route.AvailabilityForm::class,
    Route.WorkHours::class,
    Route.RosterCapacity::class, Route.Checklists::class, Route.StaffDocuments::class, Route.Policies::class,
    Route.EmployeeTimeline::class,
    Route.Courses::class, Route.CourseEditor::class, Route.TakeCourse::class, Route.CourseProgress::class, Route.OhsTraining::class
)

/**
 * SH-89: screens of one month that follow the top bar's workplace and month (RosterMonthHost)
 * instead of leaving when the workplace changes.
 */
val monthFollowingRoutes: Set<KClass<out Route>> = setOf(
    Route.RosterDetail::class, Route.RosterSkeleton::class, Route.DailyRecords::class
)

/** What a screen needs; null for screens anyone signed in may open. */
fun requiredCapability(route: KClass<out Route>): Capability? = when (route) {
    Route.RosterManagement::class, Route.RosterDetail::class, Route.RosterSkeleton::class, Route.RosterImport::class,
    Route.CreateRoster::class, Route.SkeletonEditor::class, Route.Checklists::class, Route.WorkHours::class -> Capability.EDIT_ROSTER
    Route.Team::class, Route.EmployeeForm::class, Route.PersonAccess::class, Route.StaffImport::class, Route.LeaveBalances::class,
    Route.CourseEditor::class, Route.CourseProgress::class -> Capability.MANAGE_TEAM
    Route.JobPostEditor::class -> Capability.MANAGE_TEAM
    // SH-210: a post's recruiters may be any member: the screens show only the posts they read.
    Route.Hiring::class, Route.Applicants::class -> Capability.WORK_SHIFTS
    Route.Requests::class -> Capability.APPROVE_REQUESTS
    Route.Financials::class, Route.DailyRecords::class, Route.SalesImport::class, Route.Statistics::class -> Capability.VIEW_FINANCES
    Route.Payroll::class -> Capability.PAYROLL
    Route.RosterCapacity::class -> Capability.REQUEST_AVAILABILITY
    Route.WorkplaceSettings::class -> Capability.EDIT_WORKPLACE_SETTINGS
    Route.RecurringCosts::class, Route.SharedCosts::class, Route.WorkplaceGroups::class, Route.GroupSettings::class,
    Route.Roles::class -> Capability.MANAGE_COST_RULES
    Route.Subscription::class -> Capability.BILLING
    Route.Schedule::class, Route.Availability::class, Route.AvailabilityForm::class,
    Route.Absences::class, Route.Announcements::class, Route.ShiftChecklist::class, Route.Policies::class,
    Route.Tips::class -> Capability.WORK_SHIFTS
    Route.Courses::class, Route.TakeCourse::class, Route.OhsTraining::class -> Capability.WORK_SHIFTS
    else -> null
}

/** True when [route] may be opened with [capabilities]. */
fun canOpen(route: KClass<out Route>, capabilities: Set<Capability>): Boolean =
    requiredCapability(route)?.let { it in capabilities } ?: true

/**
 * True when the back stack holds a screen the role no longer allows (a new workplace with
 * another role, an ended engagement): the app returns home instead of showing it.
 */
fun mustLeaveForHome(backStack: List<KClass<out Route>?>, capabilities: Set<Capability>): Boolean =
    backStack.any { it != null && !canOpen(it, capabilities) }
